import { z } from 'zod';
import {
  ACTIVITY_KINDS,
  SEARCH_LIMITS,
  SWITCHER_ITEM_KINDS,
  activityFilterSchema,
  listedConversationKindSchema,
  searchSortSchema,
} from '../enums';
import { isoDateTimeSchema, uuidSchema } from './common';
import { fileDtoSchema } from './files';
import { browsedChannelDtoSchema, conversationDtoSchema, messageDtoSchema } from './messaging';
import { memberDtoSchema } from './org';
import { pageInfoSchema, paginated } from './pagination';

/**
 * Search and navigation contracts (PLAN §4 "Search (Phase 2)", "Activity", the Threads and All
 * unreads rows; SPEC §4.4). Owned by the `search` API module (apps/api/src/modules/search) and
 * the web navigation stream (features/search, switcher, shortcuts, sections). The query syntax is
 * `parseSearchQuery` in `@orbit/shared/search-query`; `parsed` below is its wire form.
 */

// ================================================================ search

/** Search queries are at most 500 characters. */
export const SEARCH_QUERY_MAX_CHARS = 500;

/** `?q=&sort=&cursor=&limit=` for every search tab. */
export const searchQuerySchema = z.object({
  q: z.string().max(SEARCH_QUERY_MAX_CHARS).default(''),
  sort: searchSortSchema.default('relevance'),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;

/** A UTF-16 `[start, end)` range into `message.plainText` (or a file name / snippet). */
export const highlightRangeSchema = z.tuple([z.number().int().min(0), z.number().int().min(0)]);
export type HighlightRange = z.infer<typeof highlightRangeSchema>;

/** One modifier as the API resolved it (the UI renders it as a chip). */
export const searchFilterDtoSchema = z.object({
  type: z.enum(['from', 'mentions', 'in', 'before', 'after', 'on', 'during', 'has', 'is']),
  /** As typed, e.g. `from:@mohit`. */
  raw: z.string(),
  start: z.number().int().min(0),
  end: z.number().int().min(0),
  /** What it resolved to (a person, a conversation), when it names one. */
  resolved: z
    .object({
      kind: z.enum(['person', 'conversation']),
      id: uuidSchema,
      label: z.string(),
    })
    .nullable(),
});
export type SearchFilterDto = z.infer<typeof searchFilterDtoSchema>;

/** Problems the UI underlines without failing the search (`from:@nobody`). */
export const searchWarningSchema = z.object({
  code: z.enum(['unknown_user', 'unknown_channel', 'not_visible', 'unsupported_filter']),
  token: z.string(),
  start: z.number().int().min(0),
  end: z.number().int().min(0),
});
export type SearchWarning = z.infer<typeof searchWarningSchema>;

export const parsedSearchDtoSchema = z.object({
  /** The free-text part sent to `websearch_to_tsquery`. */
  text: z.string(),
  prefixTerms: z.array(z.string()),
  filters: z.array(searchFilterDtoSchema),
});
export type ParsedSearchDto = z.infer<typeof parsedSearchDtoSchema>;

/** The conversation a hit lives in (enough to label it; open it with `GET /conversations/:id`). */
export const searchConversationRefSchema = z.object({
  id: uuidSchema,
  kind: listedConversationKindSchema,
  /** Channels only. */
  name: z.string().nullable(),
  /** DM and group DM participants other than the viewer. */
  dmUserIds: z.array(uuidSchema),
});
export type SearchConversationRefDto = z.infer<typeof searchConversationRefSchema>;

export const searchMessageHitSchema = z.object({
  message: messageDtoSchema,
  conversation: searchConversationRefSchema,
  /** Into `message.plainText`, or into `snippet` when the message is longer than 600 characters. */
  highlights: z.array(highlightRangeSchema),
  /** Set for long messages: `ts_headline` fragments joined with " … ". */
  snippet: z.string().nullable(),
  /** Null with `sort=recent`. */
  score: z.number().nullable(),
});
export type SearchMessageHit = z.infer<typeof searchMessageHitSchema>;

/** GET /search/messages. */
export const searchMessagesResponseSchema = z.object({
  items: z.array(searchMessageHitSchema),
  nextCursor: z.string().nullable(),
  page: pageInfoSchema.optional(),
  /** At most 1,001 ("1,000+" beyond 1,000). */
  totalEstimate: z.number().int().nonnegative(),
  parsed: parsedSearchDtoSchema,
  warnings: z.array(searchWarningSchema),
  /** The statement timeout hit; results come from the windows already searched. */
  truncated: z.boolean(),
});
export type SearchMessagesResponse = z.infer<typeof searchMessagesResponseSchema>;

export const searchFileHitSchema = z.object({
  file: fileDtoSchema,
  /** The message that shares it (the one the viewer can read), if any. */
  messageId: uuidSchema.nullable(),
  conversation: searchConversationRefSchema.nullable(),
  sharedBy: uuidSchema.nullable(),
  sharedAt: isoDateTimeSchema,
  /** Into `file.name`. */
  nameHighlights: z.array(highlightRangeSchema),
  /** A fragment of the extracted text (PDF, DOCX, TXT, CSV, Markdown) with its highlights. */
  snippet: z.string().nullable(),
  snippetHighlights: z.array(highlightRangeSchema),
});
export type SearchFileHit = z.infer<typeof searchFileHitSchema>;

/** GET /search/files. */
export const searchFilesResponseSchema = z.object({
  items: z.array(searchFileHitSchema),
  nextCursor: z.string().nullable(),
  page: pageInfoSchema.optional(),
  parsed: parsedSearchDtoSchema,
  warnings: z.array(searchWarningSchema),
});
export type SearchFilesResponse = z.infer<typeof searchFilesResponseSchema>;

/** GET /search/channels (member+): public channels and the private ones I am in. */
export const searchChannelsResponseSchema = paginated(browsedChannelDtoSchema);
export type SearchChannelsResponse = z.infer<typeof searchChannelsResponseSchema>;

/** GET /search/people: the same matching as `GET /members`. */
export const searchPeopleResponseSchema = paginated(memberDtoSchema);
export type SearchPeopleResponse = z.infer<typeof searchPeopleResponseSchema>;

const tabCount = z.object({
  /** Capped at 1,001. */
  count: z.number().int().nonnegative(),
  /** More than 1,000. */
  capped: z.boolean(),
});

/** GET /search/counts — the tab badges. `tasks` is `{ count: 0 }` while no tasks provider is registered (D-362). */
export const searchCountsResponseSchema = z.object({
  messages: tabCount,
  files: tabCount,
  channels: tabCount,
  people: tabCount,
  tasks: tabCount,
});
export type SearchCountsResponse = z.infer<typeof searchCountsResponseSchema>;

// ================================================================ quick switcher (Cmd/Ctrl+K)

export const switcherQuerySchema = z.object({
  q: z.string().max(100).default(''),
  limit: z.coerce.number().int().min(1).max(20).default(20),
});
export type SwitcherQuery = z.infer<typeof switcherQuerySchema>;

export const switcherItemSchema = z.object({
  /** `recent` for the empty-query list; a person result opens (or creates) the DM. */
  kind: z.enum(SWITCHER_ITEM_KINDS),
  conversationId: uuidSchema.nullable(),
  userId: uuidSchema.nullable(),
  label: z.string(),
  sublabel: z.string().nullable(),
  /** UTF-16 ranges into `label` that matched the query. */
  matchRanges: z.array(highlightRangeSchema),
  hasUnread: z.boolean(),
  score: z.number(),
});
export type SwitcherItem = z.infer<typeof switcherItemSchema>;

export const switcherResponseSchema = z.object({ items: z.array(switcherItemSchema) });
export type SwitcherResponse = z.infer<typeof switcherResponseSchema>;

// ================================================================ Recent searches (D-1262)

/**
 * A search this person ran, kept VERBATIM with its modifiers so the box can draw `in:@asha` as a
 * chip and put the text back exactly as it was typed.
 */
export const recentSearchSchema = z.object({
  id: uuidSchema,
  query: z.string(),
  searchedAt: isoDateTimeSchema,
});
export type RecentSearch = z.infer<typeof recentSearchSchema>;

/** GET /search/recents — newest first, at most `SEARCH_LIMITS.maxRecents`. */
export const recentSearchesResponseSchema = z.object({ items: z.array(recentSearchSchema) });
export type RecentSearchesResponse = z.infer<typeof recentSearchesResponseSchema>;

/**
 * POST /search/recents — remember one. Running the same search twice moves it to the top rather
 * than adding a second row, and the list is trimmed on every write.
 */
export const rememberSearchRequestSchema = z.strictObject({
  query: z.string().trim().min(1).max(SEARCH_LIMITS.maxQueryChars),
});
export type RememberSearchRequest = z.infer<typeof rememberSearchRequestSchema>;

export const recentSearchIdParamsSchema = z.object({ recentId: uuidSchema });
export type RecentSearchIdParams = z.infer<typeof recentSearchIdParamsSchema>;

// ================================================================ All unreads (Cmd/Ctrl+Shift+A)

export const unreadsQuerySchema = z.object({
  perConversation: z.coerce.number().int().min(1).max(50).default(20),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type UnreadsQuery = z.infer<typeof unreadsQuerySchema>;

export const unreadGroupSchema = z.object({
  conversation: conversationDtoSchema,
  lastReadSeq: z.number().int().nonnegative(),
  /** Channel-visible unread messages, capped at 100 (the UI shows 99+). */
  unreadCount: z.number().int().min(0).max(100),
  /** Oldest first, from `lastReadSeq + 1`. */
  messages: z.array(messageDtoSchema),
  hasMore: z.boolean(),
});
export type UnreadGroup = z.infer<typeof unreadGroupSchema>;

/** GET /me/unreads. Each group is marked read with `POST /conversations/:id/read`. */
export const unreadsResponseSchema = paginated(unreadGroupSchema);
export type UnreadsResponse = z.infer<typeof unreadsResponseSchema>;

// ================================================================ Threads view

export const threadsQuerySchema = z.object({
  unreadOnly: z
    .enum(['true', 'false', '1', '0'])
    .transform((v) => v === 'true' || v === '1')
    .default(false),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ThreadsQuery = z.infer<typeof threadsQuerySchema>;

export const threadViewItemSchema = z.object({
  root: messageDtoSchema,
  conversation: searchConversationRefSchema,
  /** Newest last, at most 3. */
  latestReplies: z.array(messageDtoSchema),
  unreadReplyCount: z.number().int().nonnegative(),
  unreadMentionCount: z.number().int().nonnegative(),
  lastReplyAt: isoDateTimeSchema.nullable(),
});
export type ThreadViewItem = z.infer<typeof threadViewItemSchema>;

/** GET /threads — followed threads, newest reply first. */
export const threadsResponseSchema = paginated(threadViewItemSchema);
export type ThreadsResponse = z.infer<typeof threadsResponseSchema>;

// ================================================================ Activity

export const activityQuerySchema = z.object({
  filter: activityFilterSchema.default('all'),
  /**
   * D-1189: "unread" is a MODIFIER, not a tab. It composes with whichever filter is chosen —
   * unread DMs, unread mentions — which is the question people actually ask; as a tab of its own
   * it could only answer "unread anything" and cost you the tab you were on.
   *
   * `z.coerce.boolean()` would read "false" as true (a non-empty string), so the values are
   * spelled out, as `projects.ts` does for `mine`.
   */
  unreadOnly: z
    .enum(['true', 'false', '1', '0'])
    .optional()
    .transform((value) => value === 'true' || value === '1'),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(30),
});
export type ActivityQuery = z.infer<typeof activityQuerySchema>;

export const activityItemDtoSchema = z.object({
  id: uuidSchema,
  kind: z.enum(ACTIVITY_KINDS),
  actorId: uuidSchema.nullable(),
  conversationId: uuidSchema.nullable(),
  /** The message it is about (null for task and PTSTDM kinds, or when it was deleted). */
  message: messageDtoSchema.nullable(),
  /** Phase 4. */
  taskId: uuidSchema.nullable(),
  /** Kind-specific extras (`{ emoji }` for reactions). */
  payload: z.record(z.string(), z.unknown()),
  preview: z.string(),
  createdAt: isoDateTimeSchema,
  readAt: isoDateTimeSchema.nullable(),
});
export type ActivityItemDto = z.infer<typeof activityItemDtoSchema>;

/** GET /activity. */
export const activityListResponseSchema = paginated(activityItemDtoSchema);
export type ActivityListResponse = z.infer<typeof activityListResponseSchema>;

/** POST /activity/read — `ids`, or everything up to `upToId`, or everything. */
export const markActivityReadRequestSchema = z
  .strictObject({
    ids: z.array(uuidSchema).min(1).max(200).optional(),
    upToId: uuidSchema.optional(),
  })
  .refine((body) => !(body.ids && body.upToId), 'Send ids or upToId, not both');
export type MarkActivityReadRequest = z.infer<typeof markActivityReadRequestSchema>;

export const activityUnreadCountResponseSchema = z.object({
  unreadCount: z.number().int().nonnegative(),
});
export type ActivityUnreadCountResponse = z.infer<typeof activityUnreadCountResponseSchema>;
